import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '@env/environment';

import { io } from 'socket.io-client';
import {LoginService} from '@app/service/login.service';
import {EventEnum} from '@app/enums/event.enum';
import {Office} from '@app/domain/office';
import {PrintPdf} from '@app/domain/print-pdf';
import {PrintEscPos} from '@app/domain/print-esc-pos';
import {Socket} from 'socket.io-client/build/esm/socket';

@Injectable({
    providedIn: 'root'
})
export class SocketIoService {

    private socket: Socket;
    private currentOfficeCode: string;
    private eventSubjects = new Map<EventEnum, Subject<any>>();

    constructor(private loginService: LoginService) {
    }

    get connected(): boolean {
        return this.socket != null && (this.socket.connected || this.socket.active);
    }

    public initSocket(pv: Office, accessToken?: string): void {
        if (pv) {
            const officeCode = `${pv.codigo ? pv.codigo : 'PV001'}`;
            if (this.socket
                && this.currentOfficeCode === officeCode
                && (this.socket.connected || this.socket.active)) {
                return;
            }
            if (this.socket) {
                this.socket.disconnect();
            }
            const params = {
                auth_token: (accessToken || this.loginService.getToken()).replace('Bearer ', ''),
                officeCode,
                clientType: 'WEB'
            };
            // @ts-ignore
            this.socket = io(environment.wsEndpoint,
                {
                    secure: true,
                    query: params,
                    path: '/tekiwss',
                    timeout: 4000,
                    ackTimeout: 3000
                });
            this.currentOfficeCode = officeCode;
            this.eventSubjects.forEach((subject, event) => this.bindEvent(event, subject));
        }
    }

    public printCoffePdf(printPdf: PrintPdf): void {
        this.socket.emit('printPdf', printPdf);
    }

    public printCoffeEscPos(printEscPos: PrintEscPos): void {
        this.socket.emit('printEscPos', printEscPos);
    }

    public onTicket(): Observable<{}> {
        return new Observable<{}>(observer => {
            this.socket.on('ticket', (data, callback) => {
                observer.next(data);
                callback(true);
            });
        });
    }

    public onCallAutomatic(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            this.socket.on('cambioLlamadoAutomatico', (data) => observer.next(data));
        });
    }

    public onCloseReason(): Observable<string> {
        return new Observable<string>(observer => {
            this.socket.on('closeReason', (data) => observer.next(data));
        });
    }

    public onEvent(event: EventEnum): Observable<any> {
        let subject = this.eventSubjects.get(event);
        if (!subject) {
            subject = new Subject<any>();
            this.eventSubjects.set(event, subject);
        }
        this.bindEvent(event, subject);
        return subject.asObservable();
    }

    public disconnect(): void {
        if (this.socket) {
            this.socket.disconnect();
        }
    }

    public reconnect(pv: Office, accessToken?: string): void {
        this.disconnect();
        this.currentOfficeCode = undefined;
        this.initSocket(pv, accessToken);
    }

    private bindEvent(event: EventEnum, subject: Subject<any>): void {
        if (!this.socket) {
            return;
        }
        this.socket.off(event);
        this.socket.on(event, data => subject.next(data));
    }
}
